import { cx } from '@linaria/core';
import { cloneElement, PointerEvent, ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PAPER_HEIGHTS } from '../theme';
import { useAnchoredPosition } from './anchoredPosition';
import { useMountTransition } from './transitions';
import "./Tooltip.linaria.module.css!=!./Tooltip?./Tooltip.linaria.module.css?css=H4sIAAAAAAAAA41Uy67bIBTc9ysiVVe5lUJkYidx3FU3V930GxDYYGNjjgP4rf57lYedOK3UuzyHmWEOMGypK0kTJJkbCOz9WPMRIQegnKwQo3GRGqh1EjXUvP9r4dv331+2F42yIFif3MGwsQIrnQQdCdnxZAK4gsikEmkwOqgib2qL89RWXLhH/+iT2jbyVI4MOmTlIHUaMTAJN4hBN8Fyn+Ayb/IhHUvaoVYmLot8z6tmhAKC+yAAJV7KsaJJInWKLn6CJwJfEvhfBAbOQbngFES4VKrdspoZ19nCJ7xa4NUr3sg0WxB2yf16ltX4uAkUgwLz/4va5/M89+M0NJG1fR7nqAg++KHkerypfv34+JgWWUcwtEWSs5dyFKAdErSUqo/Wv0A7y42hbr1Z/+Sq4U7GdL1Z/zCSqvXGUm2R5UaKSTgOCbZFRnN4KW/CVg48wvjhMraECZ2CWFY3dMuvR7j35heV+gQHTVeK8qUcldQcZTcC3ga8nCjGJzg7QF4Po+OdQ1TJVEcx146bCVMMBGeamjOM0HAjFLSoNbSKmOG0QC2YOQJlTlLVt6IcK5AXDcQbrp2NNGg+6wVTIKCisXT9IxN1THZCHGQ/OkP1PWR31GrneaVdxTWTMWJ8kNy8e9tgs/I2K2+726zwt6cdCnbUzTDvgKclfPLD/NwR3PciiY9RRIXjZoxBO65dtF7P73FwYecT63QbhhNsDj5lFlTt5pnCkxRDRrC1TCbZBL+GYu+9TagE49Iagr2iZm5G3R5ptK+6lQUlk9V19ooart1su6EZTnySAQ9dPTGvQEUdj9Dee1vNx4j7sjp5Z4LBnmHXzHCoIuw97GDMdl3RPudu2Vo6vHwjn80gzrJzQcWrg/u/sjBhe5CxWJhYtF5M3CQ+4eMPIJn1i/kFAAA="
const TOOLTIP_TRANSITION = 200;

/** clears the trigger by the same distance MUI's tooltip margin did */
const DEFAULT_GAP = 14;

/** a long-press tooltip would blink out the instant the finger lifted */
const LEAVE_TOUCH_DELAY = 1500;
const PLACEMENTS = {
  top: {
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'center'
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'center'
    }
  },
  bottom: {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'center'
    }
  }
} as const;
const bubbleStyles = "atm_v4dhtz_o53cne atm_mk_1n9t6rb atm_tk_idpfg4 atm_fq_idpfg4 atm_73_usvi9m atm_j3_1mjvjzg atm_lo_1y44olf atm_le_1y44olf atm_lk_ftgil2 atm_ll_ftgil2 atm_2d_o53cne atm_5j_1y44olf atm_7l_1638ien atm_bx_1owkdjb atm_c8_1skhajo atm_cs_bfngof atm_g3_14vxmfm atm_r3_1h6ojuz atm_kz_1hnarqo atm_mj_glywfm atm_k4_idpfg4 atm_uc_2ff6iy";
const bubbleEnteredStyles = "atm_k4_kb7nvz";

/* the arrow is centred on the bubble, which is where the anchor is unless the
 * bubble had to be nudged off a viewport edge */
const arrowStyles = "atm_1938jqx_1yyfdc7 atm_2zt8x3_stnw88 atm_89ifzh_1ssbidh atm_d11msr_10kubth atm_1vah1d3_hoe8tu";
const arrowAboveStyles = "atm_1ymp90q_1osqo2v atm_11b2xkw_o53cne";
const arrowBelowStyles = "atm_1hhqkaf_1osqo2v atm_1syoicf_o53cne";
export interface TooltipProps {
  arrow?: boolean;
  /** a single element; it becomes the trigger and gets pointer handlers */
  children: ReactElement;
  /** lands on the bubble — override `--tooltip-background` to recolor the arrow too */
  className?: string;
  enterDelay?: number;
  /** long-press delay; omitted means touch never opens the tooltip */
  enterTouchDelay?: number;
  gap?: number;
  placement?: 'top' | 'bottom';
  /** empty leaves the trigger bare, with no listeners at all */
  title?: ReactNode;
  zIndex?: number;
}
const Tooltip = (props: TooltipProps) => {
  const {
    arrow = false,
    children,
    className,
    enterDelay = 100,
    enterTouchDelay,
    gap = DEFAULT_GAP,
    placement = 'bottom',
    title,
    zIndex = PAPER_HEIGHTS.tooltip
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const {
    anchorOrigin,
    transformOrigin
  } = PLACEMENTS[placement];
  const {
    mounted,
    entered
  } = useMountTransition(Boolean(anchorEl), TOOLTIP_TRANSITION);
  const {
    position,
    targetRef
  } = useAnchoredPosition(anchorEl, mounted, anchorOrigin, transformOrigin, {
    gap
  });
  const hide = useCallback((delay = 0) => {
    clearTimeout(timer.current);
    if (!delay) {
      setAnchorEl(null);
      return;
    }
    timer.current = setTimeout(() => setAnchorEl(null), delay);
  }, []);
  const onPointerLeave = useCallback((event: PointerEvent<HTMLElement>) => {
    hide(event.pointerType === 'touch' ? LEAVE_TOUCH_DELAY : 0);
  }, [hide]);
  const onPointerEnter = useCallback((event: PointerEvent<HTMLElement>) => {
    const touch = event.pointerType === 'touch';
    if (touch && enterTouchDelay === undefined) return;
    const {
      currentTarget
    } = event;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAnchorEl(currentTarget), touch ? enterTouchDelay : enterDelay);
  }, [enterDelay, enterTouchDelay]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const hasTitle = title !== undefined && title !== null && title !== '';
  if (!hasTitle) return children;
  return <>
            {cloneElement(children as ReactElement<Record<string, unknown>>, {
      onPointerCancel: () => hide(),
      onPointerEnter,
      onPointerLeave
    })}
            {mounted && createPortal(<div className={cx('mfc-tooltip', bubbleStyles, entered && bubbleEnteredStyles, arrow && arrowStyles, arrow && (placement === 'top' ? arrowAboveStyles : arrowBelowStyles), className)} ref={targetRef} role="tooltip" style={{
      left: position?.left,
      top: position?.top,
      visibility: position ? undefined : 'hidden',
      zIndex
    }}>
                        {title}
                    </div>, document.body)}
        </>;
};
export default Tooltip;